import {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
  useRef,
} from "react";

// Define the complete form data structure
export interface FormData {
  firstName: string;
  lastName: string;
  email: string;
  goalFor1000: string;
  usState: string;
  creditScore: string;
  merchantAccount: string;
  debtCollections: string;
  utilityBill: string;
  phone: string;
  emailConsent: string;
  referral: string; // Q9: referral question
}

export interface ContextProps {
  tab: number;
  setTab: React.Dispatch<React.SetStateAction<number>>;
  isReversed: boolean;
  setIsReversed: React.Dispatch<React.SetStateAction<boolean>>;
  formData: FormData;
  updateFormData: (field: keyof FormData, value: string) => void;
  handleClickForward: (updatedFormData?: Partial<FormData>) => void;
  globalErrors: any;
  setGlobalErrors: any;
  clearFormData: () => void;
  submitFormData: (updatedFormData?: Partial<FormData>) => Promise<void>;
  sendBookingToCrm: (inviteeUri: string) => void;
}

export interface ProviderProps {
  children: JSX.Element;
}

const FormContext = createContext<ContextProps | {}>({});

// EL3VN CRM (app.el3vn.io): leads land there at three moments.
//  started:   they typed their email (so drop-offs can be followed up)
//  submitted: they answered the last question
//  booked:    they booked the intro call in the Calendly widget on the results page
const CRM_LEAD_URL = "https://app.el3vn.io/api/crm/lead";

type CrmEvent = "started" | "submitted" | "booked";

const sendToCrm = async (data: FormData, event: CrmEvent, extra: Record<string, unknown> = {}) => {
  try {
    const response = await fetch(CRM_LEAD_URL, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      keepalive: true,
      body: JSON.stringify({
        event,
        email: data.email,
        first_name: data.firstName,
        last_name: data.lastName,
        phone: data.phone === "None" ? "" : data.phone,
        answers: event !== "submitted" ? {} : {
          "What do you want to use the $1,000 a month for?": data.goalFor1000,
          "U.S. State of legal residence": data.usState,
          "Credit score": data.creditScore,
          "Merchant processing account": data.merchantAccount,
          "Debt collections, judgments, liens or government assistance": data.debtCollections,
          "Utility bill matching driver's license address": data.utilityBill,
          "Referred by": data.referral,
          "Consents to email": data.emailConsent,
        },
        page: window.location.href,
        website: "", // spam trap, must stay empty
        ...extra,
      }),
    });
    if (!response.ok) console.error("CRM lead failed:", response.status, await response.text());
  } catch (error) {
    // Never block the form on the CRM.
    console.error("CRM lead error:", error);
  }
};

// Constants for localStorage keys
const FORM_DATA_KEY = "el3vn_form_data";
const FORM_TAB_KEY = "el3vn_form_tab";

// Helper functions for localStorage
const saveToLocalStorage = (key: string, data: any) => {
  try {
    localStorage.setItem(key, JSON.stringify(data));
  } catch (error) {
    console.warn("Failed to save to localStorage:", error);
  }
};

const loadFromLocalStorage = (key: string, defaultValue: any) => {
  try {
    const saved = localStorage.getItem(key);
    if (!saved) return defaultValue;

    const parsed = JSON.parse(saved);
    // Additional validation for formData - should be an object, not array
    if (
      key === FORM_DATA_KEY &&
      (typeof parsed !== "object" || parsed === null || Array.isArray(parsed))
    ) {
      console.warn("Invalid formData format in localStorage, using default");
      return defaultValue;
    }

    return parsed;
  } catch (error) {
    console.warn("Failed to load from localStorage:", error);
    return defaultValue;
  }
};

const FormContextProvider = ({ children }: ProviderProps) => {
  const [globalErrors, setGlobalErrors] = useState({});
  const isNavigatingRef = useRef(false);

  // Initialize form data with empty structure
  const [formData, setFormData] = useState<FormData>(() => {
    const savedData = loadFromLocalStorage(FORM_DATA_KEY, {
      firstName: "",
      lastName: "",
      email: "",
      goalFor1000: "",
      usState: "",
      creditScore: "",
      merchantAccount: "",
      debtCollections: "",
      utilityBill: "",
      phone: "",
      emailConsent: "",
      referral: "", // Q9: referral question
    });
    return savedData;
  });

  // Initialize tab from localStorage or 0
  const [tab, setTab] = useState(() => {
    const savedTab = loadFromLocalStorage(FORM_TAB_KEY, 0);

    // Show a brief notification if we're restoring progress
    if (savedTab > 0) {
      setTimeout(() => {
        console.log(
          "🔄 Progress restored! Continuing from where you left off..."
        );
      }, 100);
    }

    return savedTab;
  });

  const [isReversed, setIsReversed] = useState(false);

  // Function to update form data
  const updateFormData = useCallback((field: keyof FormData, value: string) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  }, []);

  // Save form data to localStorage whenever it changes
  useEffect(() => {
    saveToLocalStorage(FORM_DATA_KEY, formData);
  }, [formData]);

  // Save tab to localStorage whenever it changes
  useEffect(() => {
    saveToLocalStorage(FORM_TAB_KEY, tab);
  }, [tab]);

  // Define required fields for each question
  const getRequiredFieldsForQuestion = (
    questionNumber: number
  ): (keyof FormData)[] => {
    switch (questionNumber) {
      case 1:
        return ["firstName", "lastName"];
      case 2:
        return ["email"];
      case 3:
        return ["goalFor1000"];
      case 4:
        return ["usState"];
      case 5:
        return ["creditScore"];
      case 6:
        return ["merchantAccount"];
      case 7:
        return ["debtCollections"];
      case 8:
        return ["utilityBill"];
      case 9:
        return ["referral"]; // Now required (will auto-fill "None" if empty)
      case 10:
        return ["phone"]; // Now required (will auto-fill "None" if empty)
      case 11:
        return ["emailConsent"];
      case 12:
        return []; // Results page
      default:
        return [];
    }
  };

  const handleClickForward = useCallback(
    (updatedFormData?: Partial<FormData>) => {
      // Prevent multiple rapid submissions
      if (isNavigatingRef.current) {
        console.log("Navigation blocked: already navigating");
        return;
      }

      isNavigatingRef.current = true;
      setIsReversed(false);

      // Use the updated form data if provided, otherwise use current form data
      const dataToCheck = updatedFormData
        ? { ...formData, ...updatedFormData }
        : formData;

      console.log("Current tab:", tab);
      console.log("Form data to check:", dataToCheck);
      console.log("Updated form data received:", updatedFormData);

      // Helper function to check if all required fields for a question are filled
      const isQuestionCompleteWithData = (
        questionNumber: number,
        data: FormData
      ): boolean => {
        const requiredFields = getRequiredFieldsForQuestion(questionNumber);
        const isComplete = requiredFields.every(
          (field) => data[field] && data[field].trim() !== ""
        );
        console.log(
          `Question ${questionNumber} required fields:`,
          requiredFields
        );
        console.log(
          `Question ${questionNumber} values:`,
          requiredFields.map(
            (field) =>
              `${field}: "${data[field]}" (type: ${typeof data[field]})`
          )
        );
        console.log(`Question ${questionNumber} complete:`, isComplete);
        return isComplete;
      };

      // Since all questions are now required, use simplified logic
      const findFirstIncompleteQuestionWithData = (data: FormData): number => {
        for (let i = 1; i <= 11; i++) {
          const requiredFields = getRequiredFieldsForQuestion(i);
          if (
            requiredFields.length > 0 &&
            !isQuestionCompleteWithData(i, data)
          ) {
            console.log(`First incomplete question found: ${i}`);
            return i;
          }
        }
        console.log("All required questions are complete");
        return -1; // All required questions are complete
      };

      // They just entered their email: add them to the CRM now, in case they don't finish.
      if (updatedFormData?.email && !dataToCheck.emailConsent) {
        sendToCrm(dataToCheck, "started");
      }

      const firstIncomplete = findFirstIncompleteQuestionWithData(dataToCheck);

      if (firstIncomplete !== -1) {
        // Go to the first incomplete required question
        console.log(`Navigating to incomplete question: ${firstIncomplete}`);
        setTab(firstIncomplete);
      } else {
        // All required questions are complete, proceed to next question or results
        const nextTab = tab + 1;
        console.log(`Proceeding to next tab: ${nextTab}`);
        setTab(nextTab > 11 ? 12 : nextTab);
      }

      // Reset navigation state immediately
      console.log("Resetting navigation state");
      isNavigatingRef.current = false;
    },
    [formData, tab]
  );

  // Function to clear all saved data (useful for form completion or reset)
  const clearFormData = useCallback(() => {
    try {
      localStorage.removeItem(FORM_DATA_KEY);
      localStorage.removeItem(FORM_TAB_KEY);
      setFormData({
        firstName: "",
        lastName: "",
        email: "",
        goalFor1000: "",
        usState: "",
        creditScore: "",
        merchantAccount: "",
        debtCollections: "",
        utilityBill: "",
        phone: "",
        emailConsent: "",
        referral: "", // Q9: referral question
      });
      setTab(0);
    } catch (error) {
      console.warn("Failed to clear localStorage:", error);
    }
  }, []);

  // Clean up corrupted data on initialization
  useEffect(() => {
    try {
      const savedData = localStorage.getItem(FORM_DATA_KEY);
      if (savedData) {
        const parsed = JSON.parse(savedData);
        // FormData should be an object, not array
        if (
          typeof parsed !== "object" ||
          parsed === null ||
          Array.isArray(parsed)
        ) {
          console.warn("Corrupted form data detected, clearing localStorage");
          clearFormData();
        }
      }
    } catch (error) {
      console.warn("Error validating localStorage data, clearing:", error);
      clearFormData();
    }
  }, [clearFormData]);

  // Final submit goes to the EL3VN CRM. The last answer is passed in because its state update hasn't landed yet.
  const submitFormData = useCallback(
    async (updatedFormData?: Partial<FormData>): Promise<void> => {
      await sendToCrm({ ...formData, ...updatedFormData }, "submitted");
    },
    [formData]
  );

  const sendBookingToCrm = useCallback(
    (inviteeUri: string) => {
      sendToCrm(formData, "booked", { calendly: { invitee_uri: inviteeUri } });
    },
    [formData]
  );

  return (
    <FormContext.Provider
      value={{
        tab,
        setTab,
        isReversed,
        setIsReversed,
        formData,
        updateFormData,
        handleClickForward,
        globalErrors,
        setGlobalErrors,
        clearFormData,
        submitFormData,
        sendBookingToCrm,
      }}
    >
      {children}
    </FormContext.Provider>
  );
};

export default FormContextProvider;

export const useFormContext = () => useContext(FormContext) as ContextProps;
